import { useQuery } from "@tanstack/react-query";
import { AvField, AvInput } from "availity-reactstrap-validation";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next"
import { toast } from "react-toastify";
import { Col, Label, Row } from "reactstrap";
import IndustriesService from "src/modules/3rd-party-management/apis/common/IndustriesService";
import Select from "react-select";
import DatePicker from "react-datepicker";
import * as datePickerLocales from "date-fns/locale";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants";

const GeneralInfos = ({ 
    t, 
    lng, 
    handleFetchCompanyDetailQuery, 
    countriesListIsLoading, 
    memoizedCountriesList 
}) => {
    const companyDetails = handleFetchCompanyDetailQuery.data?.companyDetail;
    const isDetailsLoading = handleFetchCompanyDetailQuery.isFetching;

    const dateUtils = new DateUtils();

    const [ industries, setIndustries ] = useState([]);
    const [ companySize, setCompanySize ] = useState(null);
    const [birthDate, setBirthDate] = useState(null);
    const [birthCountry, setBirthCountry] = useState(null)

    const handleFetchIndustriesListQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-industries-list-query',
            lng
        ],
        queryFn: async () => {
            const service = IndustriesService.getInstance();

            return await service.fetchList({
                language: lng !== 'en' ? lng : undefined,
                pageSize: 1000,
                pageIndex: 1
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: companyDetails?.type && companyDetails.type !== 'individual',
        onError: () => {
            toast(t('An error occurred while fetching industries list.'), {
                type: 'error'
            });
        }
    });

    const memorizedIndustries = useMemo(() => {
        const data = Array.isArray(handleFetchIndustriesListQuery.data) ? handleFetchIndustriesListQuery.data : [];

        return data.map((industry) => {
            return {
                value: industry.id,
                baseLabel: industry.title,
                label: `${industry.class} - ${industry.title}`
            };
        });
    }, [
        handleFetchIndustriesListQuery.data
    ]);

    useEffect(() => {
        if (memorizedIndustries.length > 0 && companyDetails?.industry?.length > 0) {
            const newSelectedIndustries = companyDetails.industry.map((industry) => {
                return memorizedIndustries.find((tIndustry) => tIndustry.value === industry.id)
            }).filter((industry) => !!industry?.value);

            setIndustries(newSelectedIndustries);
        }
    }, [
        memorizedIndustries,
        companyDetails?.industry
    ]);

    useEffect(() => {
        if (companyDetails?.companySize) {
            setCompanySize({
                value: companyDetails.companySize,
                label: companyDetails.companySize + " " + t("Employees")
            })
        }

        if (companyDetails?.individualDateOfBirth) {
            setBirthDate(new Date(companyDetails.individualDateOfBirth))
        }

        if (companyDetails?.individualBirthPlaceCountry?.id) {
            setBirthCountry({
                value: companyDetails.individualBirthPlaceCountry.id,
                baseLabel: companyDetails.individualBirthPlaceCountry.name,
                label: t(companyDetails.individualBirthPlaceCountry.name),
                code: companyDetails.individualBirthPlaceCountry.code,
            })
        }
    }, [
        companyDetails,
        t
    ]);

    const renderIndividualBody = () => {
        return (
            <Row>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-name">
                        {t("Name")}: <span className="text-danger">*</span>
                    </Label>
                    <AvField
                        id="company-details-name"
                        required
                        name={"company_details[name]"}
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: true },
                        }}
                        defaultValue={companyDetails.name}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-shortName">
                        {t("Nickname / AKA")}:
                    </Label>
                    <AvField
                        id="company-details-shortName"
                        required
                        name={"company_details[shortName]"}
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                        }}
                        defaultValue={companyDetails.shortName}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-dateOfBirth-input">
                        {t("Date of Birth")}:
                    </Label>

                    <DatePicker
                        locale={datePickerLocales[lng]}
                        name="dateOfBirth"
                        id="company-details-dateOfBirth-input"
                        className="form-control"
                        selected={birthDate}
                        showTimeSelect={false}
                        dateFormat={INTERNATIONAL_DATE_FORMAT}
                        onChange={(date) => {
                            setBirthDate(date)
                        }}
                        autoComplete="off"
                    />

                    <AvInput
                        id="company-details-dateOfBirth-input2"
                        name="company_details[individualDateOfBirth]"
                        placeholder=""
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        value={birthDate ? dateUtils.convertDateToDate(
                            birthDate, 'YYYY-MM-DD'
                        ) : ''}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-countryOfBirth-input">
                        {t("Country of Birth")}:
                    </Label>
                    <Select
                        id="company-details-countryOfBirth-input"
                        placeholder={t("Select...")}
                        classNamePrefix="select2-selection"
                        options={memoizedCountriesList}
                        menuPortalTarget={document.body}
                        onChange={(e) => {
                            setBirthCountry(e)
                        }}
                        value={birthCountry}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        isLoading={countriesListIsLoading}
                        isDisabled={countriesListIsLoading}
                    />

                    <AvInput
                        id="company-details-countryOfBirth-input2"
                        name="company_details[individualBirthPlaceCountry]"
                        placeholder=""
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        value={birthCountry?.value}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-taxId-input">
                        {t("Tax ID")}:
                    </Label>
                    <AvField
                        name="company_details[individualTaxId]"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="company-details-taxId-input"
                        defaultValue={companyDetails.individualTaxId}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-nationalId-number-input">
                        {t("National ID Number")}:
                    </Label>
                    <AvField
                        name="company_details[individualNationalIdNumber]"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="company-details-nationalId-number-input"
                        defaultValue={companyDetails.individualNationalIdNumber}
                    />
                </Col>

            </Row>
        )
    }

    const renderCompanyBody = () => {
        return (
            <Row>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-name">
                        {t("Company Name")}: <span className="text-danger">*</span>
                    </Label>
                    <AvField
                        id="company-details-name"
                        required
                        name={"company_details[name]"}
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: true },
                        }}
                        defaultValue={companyDetails.name}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-brands">
                        {t("Brands")}:
                    </Label>
                    <AvField
                        id="company-details-brands"
                        required
                        name={"company_details[brands]"}
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                        }}
                        defaultValue={companyDetails.brands}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-industry">
                        {t("Industries")}: <span className="text-danger">*</span>
                    </Label>

                    <Select
                        isMulti
                        isDisabled={true}
                        isLoading={handleFetchIndustriesListQuery.isFetching}
                        placeholder={t("Select") + "..."}
                        classNamePrefix="select2-selection"
                        id='company-details-industry'
                        options={memorizedIndustries}
                        menuPortalTarget={document.body}
                        value={industries}
                    />

                    <AvField
                        name={"company_details[industries]"}
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: true },
                        }}
                        value={industries.map((industry) => industry.value.toString())}
                    />
                </Col>

                <Col sm="12" md="6" lg="4" className="mb-3">
                    <Label for="company-details-size">
                        {t("Company Size")}: <span className="text-danger">*</span>
                    </Label>
                    <Select
                        placeholder={t("Select") + "..."}
                        classNamePrefix="select2-selection"
                        id='company-details-size'
                        options={[
                            {
                                value: "0-10",
                                label: "0-10 " + t("Employees"),
                            },
                            {
                                value: "10-50",
                                label: "10-50 " + t("Employees"),
                            },
                            {
                                value: "50-100",
                                label: "50-100 " + t("Employees"),
                            },
                            {
                                value: "100-500",
                                label: "100-500 " + t("Employees"),
                            }
                        ]}
                        menuPortalTarget={document.body}
                        onChange={(e) => {
                            setCompanySize(e)
                        }}
                        value={companySize}
                    />

                    <AvField name={"company_details[companySize]"}
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        validate={{
                            required: { value: true }
                        }}
                        value={companySize?.value}
                    />
                </Col>

                <Col sm="12" md="6" lg="8" className="mb-3">
                    <Label for="company-details-website">
                        {t("Website")}:
                    </Label>
                    <AvField name={"company_details[websiteUrl]"}
                        placeholder=""
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                            pattern: {
                                value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                errorMessage: t("Please enter a valid URL"),
                            },
                        }}
                        id="company-details-website"
                        defaultValue={companyDetails.websiteUrl}
                    />
                </Col>
            </Row>
        )
    }

    if (isDetailsLoading || !companyDetails?.type) {
        return (
            <Row>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <p style={{ width: '40%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <p style={{ width: '40%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <p style={{ width: '40%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                </Col>


                <Col sm="12" md="6" lg="4" className="mb-3">
                    <p style={{ width: '40%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <p style={{ width: '40%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col sm="12" md="6" lg="4" className="mb-3">
                    <p style={{ width: '40%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        )
    }

    if (companyDetails.type === 'individual') {
        return renderIndividualBody()
    }

    return renderCompanyBody()
}

export default withNamespaces()(
    GeneralInfos
)